import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { OptionKey } from '@common-fe/common-fe';
import { toString } from 'lodash';
import _ from 'lodash';

import { api } from '@/api';
import { Pagination } from '@/common/types';
import { API_URL, ITaxFormInfoDto } from '@/dtos';

import { ITaxForm, ITaxFormType, TaxFormHistoryItem } from '../TaxForms.types';

interface Params {
  employeeId?: string;
  years?: OptionKey[];
  page: number;
  perPage: number;
  sortBy: OptionKey;
}

export const TAX_FORMS_LIST_QUERY_KEY = 'TAX_FORMS_LIST_KEY';
const SORT_KEY = 'created_at';
const useTaxFormsListQuery = (params: Params) => {
  const { data, isLoading, isSuccess } = useQuery(
    [
      TAX_FORMS_LIST_QUERY_KEY,
      params.page,
      params.perPage,
      params.sortBy,
      ...(params?.years ? params?.years || [] : []),
    ],
    () =>
      api.get<Pagination<ITaxFormInfoDto>>(API_URL.V2_TAX_FORMS, {
        params: {
          sort: params.sortBy ? `${SORT_KEY},${params.sortBy}` : undefined,
          employee_id: params.employeeId,
          tax_years: params?.years?.length ? params?.years?.join(',') : undefined,
          page: params.page,
          size: params.perPage,
        },
      }),
    {
      enabled: !!params?.employeeId,
      retry: 0,
    }
  );

  const formattedData = useMemo<ITaxForm[]>(() => {
    const list = data?.data?.content || [];

    return list.map((item) => {
      const mailedList = item.mailing_history
        ? item.mailing_history.map((item) => {
            const mailedListItem: TaxFormHistoryItem = {
              id: `${item.id}`,
              date: new Date(item.mailed_at as unknown as string),
              status: 'Mailed',
            };
            return mailedListItem;
          })
        : [];
      const sortedMailedList = _.sortBy(mailedList, (o) => -o.date);
      const currentItem: ITaxForm = {
        id: toString(item?.id),
        name: item?.name || '',
        type: item?.type as ITaxFormType.TAX_FORM_1099,
        taxYear: item.tax_year || '',
        history: sortedMailedList,
      };
      return currentItem;
    });
  }, [data?.data]);

  return {
    total: data?.data?.totalElements || 0,
    formattedData,
    isLoading,
    isSuccess,
  };
};

export default useTaxFormsListQuery;
